import { __spreadArrays } from "tslib";
import { isNumber } from '@tyler-components-web/core';
import { CalendarView } from '../calendar/calendar-constants';
import { DateRange } from '../calendar/core/date-range';
import { POPUP_DIALOG_WIDTH_THRESHOLD } from '../constants';
var CalendarDropdownFoundation = /** @class */ (function () {
    function CalendarDropdownFoundation(_adapter) {
        var _this = this;
        this._adapter = _adapter;
        this._value = null;
        this._min = null;
        this._max = null;
        this._blockedDates = null;
        this._open = false;
        this._dialogWidthThreshold = POPUP_DIALOG_WIDTH_THRESHOLD;
        this._showToday = false;
        this._showClear = false;
        this._disabledDaysOfWeek = [];
        this._startView = CalendarView.Date;
        this._selectListener = function (evt) { return _this._onSelect(evt); };
        this._closeListener = function () { return _this._onClose(); };
    }
    CalendarDropdownFoundation.prototype.destroy = function () {
        if (this._open) {
            this._closeDropdown();
        }
    };
    CalendarDropdownFoundation.prototype.propagateKey = function (key, isShiftKey) {
        this._adapter.propagateKey(key, isShiftKey);
    };
    CalendarDropdownFoundation.prototype.setActiveDate = function (date) {
        this._adapter.setActiveDate(date);
    };
    CalendarDropdownFoundation.prototype._openDropdown = function () {
        var config = {
            value: this._value,
            minDate: this._min,
            maxDate: this._max,
            blockedDates: this._blockedDates,
            id: this._id,
            popupClasses: this._popupClasses,
            activeChangeCallback: this._activeChangeCallback,
            range: this._range,
            showToday: this._showToday,
            showClear: this._showClear,
            todayCallback: this._todayCallback,
            clearCallback: this._clearCallback,
            disabledDaysOfWeek: this._disabledDaysOfWeek || [],
            disableDayCallback: this._disableDayCallback,
            startView: this._startView
        };
        var isMobile = this._adapter.getScreenWidth() <= this._dialogWidthThreshold;
        this._adapter.open(config, isMobile, this._closeListener);
        this._adapter.setSelectListener(this._selectListener, this._range);
    };
    CalendarDropdownFoundation.prototype._closeDropdown = function () {
        this._adapter.close();
    };
    CalendarDropdownFoundation.prototype._onClose = function () {
        if (typeof this._closeCallback === 'function') {
            this._closeCallback();
        }
    };
    CalendarDropdownFoundation.prototype._onSelect = function (evt) {
        if (typeof this._selectCallback === 'function') {
            this._selectCallback(evt.detail);
        }
    };
    Object.defineProperty(CalendarDropdownFoundation.prototype, "value", {
        get: function () {
            return this._value instanceof Date ? new Date(this._value.getTime()) : this._value instanceof DateRange ? this._value.copy() : null;
        },
        set: function (value) {
            if (this._value !== value) {
                if (value instanceof Date) {
                    this._value = new Date(value.getTime());
                }
                else if (value && (value instanceof DateRange || typeof value === 'object')) {
                    this._value = new DateRange(value);
                }
                else {
                    this._value = null;
                }
                if (this._open) {
                    this._adapter.setValue(this._value);
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "min", {
        get: function () {
            return !!this._min ? new Date(this._min.getTime()) : null;
        },
        set: function (value) {
            if (this._min !== value) {
                this._min = !!value ? new Date(value.getTime()) : null;
                this._adapter.setMinDate(this._min);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "max", {
        get: function () {
            return !!this._max ? new Date(this._max) : null;
        },
        set: function (value) {
            if (this._max !== value) {
                this._max = !!value ? new Date(value.getTime()) : null;
                this._adapter.setMaxDate(this._max);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "blockedDates", {
        get: function () {
            if (!this._blockedDates) {
                return null;
            }
            if (Array.isArray(this._blockedDates)) {
                return this._blockedDates.map(function (d) { return new Date(d.getTime()); });
            }
            return new Date(this._blockedDates.getTime());
        },
        set: function (value) {
            if (this._blockedDates !== value) {
                if (!value) {
                    this._blockedDates = null;
                }
                else if (Array.isArray(value)) {
                    this._blockedDates = value.map(function (d) { return new Date(d.getTime()); });
                }
                else {
                    this._blockedDates = new Date(value.getTime());
                }
                this._adapter.setBlockedDates(this._blockedDates);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "open", {
        get: function () {
            return this._open;
        },
        set: function (value) {
            if (this._open !== value) {
                this._open = value;
                if (this._open) {
                    this._openDropdown();
                }
                else {
                    this._closeDropdown();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "id", {
        get: function () {
            return this._id;
        },
        set: function (value) {
            this._id = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "popupClasses", {
        get: function () {
            if (Array.isArray(this._popupClasses)) {
                return __spreadArrays(this._popupClasses);
            }
            return this._popupClasses;
        },
        set: function (value) {
            if (Array.isArray(value)) {
                this._popupClasses = __spreadArrays(value);
            }
            else {
                this._popupClasses = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "dialogWidthThreshold", {
        set: function (value) {
            if (!isNumber(value) || value < 0) {
                value = POPUP_DIALOG_WIDTH_THRESHOLD;
            }
            this._dialogWidthThreshold = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "range", {
        get: function () {
            return this._range;
        },
        set: function (value) {
            if (this._range !== value) {
                this._range = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "disabledDaysOfWeek", {
        get: function () {
            return this._disabledDaysOfWeek;
        },
        set: function (value) {
            if (value !== this._disabledDaysOfWeek) {
                this._disabledDaysOfWeek = value;
                this._applyDisabledDaysOfWeek();
            }
        },
        enumerable: true,
        configurable: true
    });
    CalendarDropdownFoundation.prototype._applyDisabledDaysOfWeek = function () {
        this._adapter.setDisabledDaysOfWeek(this._disabledDaysOfWeek);
    };
    Object.defineProperty(CalendarDropdownFoundation.prototype, "showToday", {
        get: function () {
            return this._showToday;
        },
        set: function (value) {
            this._showToday = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "showClear", {
        get: function () {
            return this._showClear;
        },
        set: function (value) {
            this._showClear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "closeCallback", {
        set: function (cb) {
            this._closeCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "selectCallback", {
        set: function (cb) {
            this._selectCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "activeChangeCallback", {
        set: function (cb) {
            this._activeChangeCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "todayCallback", {
        set: function (cb) {
            this._todayCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "clearCallback", {
        set: function (cb) {
            this._clearCallback = cb;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarDropdownFoundation.prototype, "disableDayCallback", {
        get: function () {
            return this._disableDayCallback;
        },
        set: function (value) {
            if (value !== this._disableDayCallback) {
                this._disableDayCallback = value;
                this._applyDisableDayCallback();
            }
        },
        enumerable: true,
        configurable: true
    });
    CalendarDropdownFoundation.prototype._applyDisableDayCallback = function () {
        this._adapter.setDisableDayCallback(this._disableDayCallback);
    };
    Object.defineProperty(CalendarDropdownFoundation.prototype, "startView", {
        get: function () {
            return this._startView;
        },
        set: function (value) {
            if (value !== this._startView) {
                this._startView = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    return CalendarDropdownFoundation;
}());
export { CalendarDropdownFoundation };
